const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;
const URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;

export const POSTHOG_PERSONAL_DATA_QUERY_KEYS = [
  'checkoutAuth',
  'checkoutBilling',
  'verifyEmail',
  'trialToken',
  'fromTrial',
  'token',
  'auth',
  'authorization',
  'code',
  'email',
  'phone',
  'name',
  'address'
];

const SENSITIVE_PROPERTY_KEY_PATTERN = /(^|_)(email|phone|first_name|last_name|full_name|buyer_name|seller_name|owner_name|contact_name|address|password|token|secret|authorization|cookie|card|cvv|cvc|ssn|social|iban|routing|fingerprint|prompt|request_body|response_body|lat|lng|latitude|longitude|coordinates|bounds|map_center|prefix|catastro|cadastral)(_|$)/i;
const URL_PROPERTY_KEY_PATTERN = /(^|\$|_)(url|uri|href|location|referrer)(_|$)/i;

export const isSensitiveAnalyticsProperty = (key) => (
  SENSITIVE_PROPERTY_KEY_PATTERN.test(String(key || '').trim())
);

export const isAnalyticsUrlProperty = (key) => (
  URL_PROPERTY_KEY_PATTERN.test(String(key || '').trim())
);

export const sanitizeAnalyticsUrl = (value) => {
  const input = String(value == null ? '' : value).trim();
  if (!input) return '';
  try {
    const base = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://www.casadata.io';
    const parsed = new URL(input, base);
    const isRelative = !/^[a-z][a-z0-9+.-]*:\/\//i.test(input);
    const path = parsed.pathname || '/';
    return (isRelative ? path : `${parsed.origin}${path}`).slice(0, 300);
  } catch {
    return input.split(/[?#]/, 1)[0].slice(0, 300);
  }
};

export const redactAnalyticsText = (value, limit = 500) => {
  let text = String(value == null ? '' : value);
  text = text.replace(URL_PATTERN, (url) => sanitizeAnalyticsUrl(url));
  text = text
    .replace(EMAIL_PATTERN, '[redacted_email]')
    .replace(PHONE_PATTERN, '[redacted_phone]')
    .replace(CARD_PATTERN, '[redacted_card]')
    .replace(TOKEN_PATTERN, '[redacted_token]')
    .replace(LONG_NUMBER_PATTERN, '[redacted_number]')
    .replace(/\s+/g, ' ')
    .trim();
  return text.slice(0, Math.max(1, Number(limit) || 500));
};

export const analyticsErrorCode = (value) => {
  const text = String(value == null ? '' : value).toLowerCase();
  if (!text) return 'unknown_error';
  if (/abort|timeout|timed out/.test(text)) return 'timeout';
  if (/network|failed to fetch|fetch failed|load failed|offline/.test(text)) return 'network_error';
  if (/unauthori[sz]ed|\b401\b|session.*expir/.test(text)) return 'unauthorized';
  if (/forbidden|\b403\b/.test(text)) return 'forbidden';
  if (/rate.?limit|too many|\b429\b/.test(text)) return 'rate_limited';
  if (/declin|payment.*fail|provider.*reject|card.*fail/.test(text)) return 'payment_rejected';
  if (/invalid|validation|malformed|missing|required/.test(text)) return 'invalid_request';
  if (/not.?found|\b404\b/.test(text)) return 'not_found';
  if (/server|\b5\d\d\b/.test(text)) return 'server_error';
  return 'request_failed';
};

export const stripSensitiveBrowserQueryParams = (keys = ['checkoutAuth', 'checkoutBilling']) => {
  if (typeof window === 'undefined' || !window.location || !window.history) return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    let changed = false;
    keys.forEach((key) => {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    });
    if (!changed) return false;
    const query = params.toString();
    const nextUrl = `${window.location.pathname || '/'}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state, document?.title || '', nextUrl);
    return true;
  } catch {
    return false;
  }
};

const analyticsPrivacy = {
  POSTHOG_PERSONAL_DATA_QUERY_KEYS,
  isSensitiveAnalyticsProperty,
  isAnalyticsUrlProperty,
  sanitizeAnalyticsUrl,
  redactAnalyticsText,
  analyticsErrorCode,
  stripSensitiveBrowserQueryParams
};

export default analyticsPrivacy;
